import type { Tab } from './index';
import { esc, setTableState } from '../utils';
import { fetchSheet } from '../worker';
import { writeToSheet } from '../sheets';
import { showToast } from '../toast';
import { store } from '../state';

interface RoadmapRow {
  _id:       string | number;
  Title:     string;
  Phase?:    string;
  Status:    string;
  Category?: string;
  Priority?: string;
  Effort?:   string;
  Notes?:    string;
}

interface FieldDef {
  key:         string;
  label:       string;
  type?:       'text' | 'select' | 'textarea';
  required?:   boolean;
  options?:    string[];
  placeholder?: string;
}

const FIELDS: FieldDef[] = [
  { key:'Title',    label:'Title',    required:true },
  { key:'Phase',    label:'Phase',    type:'select',
    options:['Phase 1a','Phase 1b','Phase 2','Phase 3','Ongoing','Backlog'] },
  { key:'Status',   label:'Status',   type:'select',
    options:['Planned','In Progress','Completed','Cancelled'] },
  { key:'Category', label:'Category', type:'select',
    options:['ML/AI','Infrastructure','UI','MCP','Quantum','Research','Security','Backlog'] },
  { key:'Priority', label:'Priority', type:'select',
    options:['High','Medium','Low'] },
  { key:'Effort',   label:'Effort',   placeholder:'e.g. 1 session' },
  { key:'Notes',    label:'Notes',    type:'textarea' },
];

const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  'Planned':     { bg:'rgba(0,212,255,0.1)',    color:'var(--accent)', border:'rgba(0,212,255,0.3)'  },
  'In Progress': { bg:'rgba(255,184,48,0.1)',   color:'var(--warn)',   border:'rgba(255,184,48,0.3)' },
  'Completed':   { bg:'rgba(46,213,115,0.1)',   color:'var(--green)',  border:'rgba(46,213,115,0.3)' },
  'Cancelled':   { bg:'rgba(122,157,181,0.08)', color:'var(--text3)',  border:'var(--border)'        },
};

const PRIORITY_COLORS: Record<string, string> = {
  High:   'var(--red)',
  Medium: 'var(--warn)',
  Low:    'var(--text3)',
};

const STATUS_ORDER: Record<string, number> = { 'In Progress':0, Planned:1, Completed:2, Cancelled:3 };

export class RoadmapTab implements Tab {
  readonly id = 'roadmap';

  private statusFilter = 'All';
  private pendingSave: ((data: Record<string, string>) => void) | null = null;

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  init(): void {
    this.el('roadmap-sync-btn').addEventListener('click', () => this.sync());
    this.el('roadmap-add-btn').addEventListener('click',  () => this.openAdd());

    // Status filter buttons via data attribute
    document.querySelectorAll<HTMLElement>('[data-rm-status]').forEach(btn =>
      btn.addEventListener('click', () => this.setStatus(btn.dataset.rmStatus ?? 'All')),
    );

    // Phase dropdown + search input re-render on change
    this.el('rmf-phase').addEventListener('change', () => this.renderTable());
    (this.el('rmf-search') as HTMLInputElement).addEventListener('input', () => this.renderTable());

    // Edit/delete via event delegation
    this.el('roadmap-tbody').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      const row = (store.get('roadmapData') as RoadmapRow[]).find(r => String(r._id) === btn.dataset.id);
      if (!row) return;
      if (btn.dataset.action === 'edit')   this.openEdit(row);
      if (btn.dataset.action === 'delete') this.delete(row);
    });
  }

  render(): void {
    const cached = store.get('roadmapData') as RoadmapRow[];
    if (cached.length) {
      this.renderTable();
    } else {
      this.sync();
    }
  }

  private async sync(): Promise<void> {
    const btn = this.el('roadmap-sync-btn') as HTMLButtonElement;
    btn.textContent = 'SYNCING…';
    btn.style.opacity = '0.6';
    try {
      const data = await fetchSheet<RoadmapRow>('Roadmap');
      if (data.status !== 'ok') throw new Error(data.message ?? 'Error loading Roadmap sheet');
      store.set('roadmapData', data.rows ?? []);
      this.renderTable();
      this.el('roadmap-sync-time').textContent =
        `${(data.rows ?? []).length} items · ${new Date().toLocaleTimeString()}`;
      showToast(`Loaded ${(data.rows ?? []).length} roadmap items`);
    } catch (err: unknown) {
      showToast(`Roadmap sync failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      btn.textContent = '↺ SYNC';
      btn.style.opacity = '1';
    }
  }

  private setStatus(val: string): void {
    this.statusFilter = val;
    ['All','Planned','In Progress','Completed','Cancelled'].forEach(s => {
      const btn = document.querySelector<HTMLElement>(`[data-rm-status="${s}"]`);
      if (!btn) return;
      const active = s === val;
      btn.style.background  = active ? 'rgba(0,212,255,0.15)' : 'transparent';
      btn.style.borderColor = active ? 'rgba(0,212,255,0.4)'  : 'var(--border)';
      btn.style.color       = active ? 'var(--accent)'        : 'var(--text3)';
    });
    this.renderTable();
  }

  private renderTable(): void {
    const phase  = (this.el('rmf-phase') as HTMLSelectElement).value;
    const search = (this.el('rmf-search') as HTMLInputElement).value.toLowerCase();
    const all    = store.get('roadmapData') as RoadmapRow[];

    let rows = all.filter(r => {
      if (this.statusFilter !== 'All' && r.Status !== this.statusFilter) return false;
      if (phase && r.Phase !== phase) return false;
      if (search && !Object.values(r).join(' ').toLowerCase().includes(search)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => (STATUS_ORDER[a.Status] ?? 99) - (STATUS_ORDER[b.Status] ?? 99));

    // Update stats from full dataset (not filtered)
    const counts: Record<string, number> = { Planned:0, 'In Progress':0, Completed:0, Cancelled:0 };
    all.forEach(r => { if (r.Status in counts) counts[r.Status]++; });
    this.el('rm-stat-planned').textContent    = String(counts['Planned']);
    this.el('rm-stat-inprogress').textContent = String(counts['In Progress']);
    this.el('rm-stat-completed').textContent  = String(counts['Completed']);
    this.el('rm-stat-cancelled').textContent  = String(counts['Cancelled']);
    this.el('rm-stat-total').textContent      = String(all.length);

    setTableState('roadmap-tbody', 'roadmap-table-wrap', 'roadmap-empty', rows, (row: RoadmapRow) => {
      const sc = STATUS_COLORS[row.Status]    ?? STATUS_COLORS['Planned'];
      const pc = PRIORITY_COLORS[row.Priority ?? ''] ?? 'var(--text3)';
      const id = String(row._id);
      return `<tr>
        <td><span style="background:${sc.bg};color:${sc.color};border:1px solid ${sc.border};padding:2px 8px;border-radius:2px;font-family:var(--mono);font-size:10px;font-weight:700;letter-spacing:0.06em;white-space:nowrap;">${esc(row.Status)}</span></td>
        <td style="color:var(--text2);font-size:11px;white-space:nowrap;">${esc(row.Phase ?? '—')}</td>
        <td><strong style="color:var(--text);">${esc(row.Title)}</strong></td>
        <td><span class="tag">${esc(row.Category ?? '—')}</span></td>
        <td style="color:${pc};font-size:11px;font-weight:700;">${esc(row.Priority ?? '—')}</td>
        <td style="color:var(--text3);font-size:11px;">${esc(row.Effort ?? '—')}</td>
        <td class="notes-cell" style="max-width:220px;font-size:11px;color:var(--text2);">${esc(row.Notes ?? '—')}</td>
        <td><div style="display:flex;gap:4px;">
          <button data-action="edit"   data-id="${id}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">Edit</button>
          <button data-action="delete" data-id="${id}" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">Del</button>
        </div></td>
      </tr>`;
    });
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────

  private openAdd(): void {
    this.showModal('Add Roadmap Item', {}, data => {
      writeToSheet('append', 'Roadmap', data).then(ok => {
        if (ok) { showToast('Added: ' + data['Title']); setTimeout(() => this.sync(), 800); }
      });
    });
  }

  private openEdit(row: RoadmapRow): void {
    this.showModal(`Edit — ${row.Title}`, row as unknown as Record<string, string>, data => {
      writeToSheet('update', 'Roadmap', { ...data, _id: row._id }).then(ok => {
        if (ok) { showToast('Updated: ' + data['Title']); setTimeout(() => this.sync(), 800); }
      });
    });
  }

  private delete(row: RoadmapRow): void {
    if (!confirm(`Delete "${row.Title}" from roadmap?`)) return;
    writeToSheet('delete', 'Roadmap', { _id: row._id }).then(ok => {
      if (ok) { showToast('Deleted'); setTimeout(() => this.sync(), 800); }
    });
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  private showModal(
    title: string,
    prefill: Record<string, string>,
    onSave: (data: Record<string, string>) => void,
  ): void {
    document.getElementById('roadmap-modal')?.remove();
    this.pendingSave = onSave;

    const base = 'width:100%;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;box-sizing:border-box;';

    const fields = FIELDS.map(f => {
      const full = f.type === 'textarea' ? 'grid-column:1/-1;' : '';
      let inp: string;
      if (f.type === 'select') {
        const opts = (f.options ?? []).map(o =>
          `<option value="${esc(o)}"${prefill[f.key] === o ? ' selected' : ''}>${esc(o)}</option>`
        ).join('');
        inp = `<select id="rm-f-${f.key}" style="${base}">${opts}</select>`;
      } else if (f.type === 'textarea') {
        inp = `<textarea id="rm-f-${f.key}" rows="3" style="${base}resize:vertical;">${esc(prefill[f.key] ?? '')}</textarea>`;
      } else {
        inp = `<input id="rm-f-${f.key}" type="text" value="${esc(prefill[f.key] ?? '')}" placeholder="${esc(f.placeholder ?? '')}" style="${base}">`;
      }
      return `<div style="${full}">
        <label style="display:block;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:4px;">${esc(f.label)}${f.required ? ' *' : ''}</label>
        ${inp}
      </div>`;
    }).join('');

    const modal = document.createElement('div');
    modal.id = 'roadmap-modal';
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.82);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;';
    modal.innerHTML = `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:24px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;">
        <div style="font-size:16px;font-weight:700;color:var(--text);margin-bottom:18px;">${esc(title)}</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">${fields}</div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px;">
          <button id="rm-modal-cancel" style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:8px 16px;cursor:pointer;border-radius:3px;">CANCEL</button>
          <button id="rm-modal-save"   style="background:rgba(46,213,115,0.1);border:1px solid rgba(46,213,115,0.3);color:var(--green);font-family:var(--mono);font-size:11px;padding:8px 20px;cursor:pointer;border-radius:3px;">SAVE</button>
        </div>
      </div>`;

    document.body.appendChild(modal);
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
    modal.querySelector('#rm-modal-cancel')!.addEventListener('click', () => modal.remove());
    modal.querySelector('#rm-modal-save')!.addEventListener('click',   () => this.submitModal());
    setTimeout(() => (document.getElementById('rm-f-Title') as HTMLInputElement | null)?.focus(), 100);
  }

  private submitModal(): void {
    if (!this.pendingSave) return;
    const data: Record<string, string> = {};
    FIELDS.forEach(f => {
      const el = document.getElementById(`rm-f-${f.key}`) as HTMLInputElement | null;
      if (el) data[f.key] = el.value.trim();
    });
    if (!data['Title']) { showToast('Title is required', 'warn'); return; }
    document.getElementById('roadmap-modal')?.remove();
    this.pendingSave(data);
    this.pendingSave = null;
  }
}
